import {
    BaseType,
    BuildComponentType,
    CardinalDirection, ColorEnum, ElevatorType, HazardousState, HeritageProtectionLevel, LevelType,
    RiskConstructionType,
    RoofShape,
    RoofSafetyStatus,
    RoomType,
    UtilityCategory,
    OutdoorCategory,
    WindowPaneType
} from "./Enums";
import { BuildingType } from "./BuildingTypeEnums";
import { ComponentType } from "react";
import { AnyGeometryShape } from "./GeometryShapes";
import type { CustomTimelineItem } from "./timeline";
import { DocumentModel } from "../api/buildingDocumentsApi";
import { translate } from "./EnumTranslations";
import { TodoItem } from "./todos";
import { FileAttachment, Attachment } from "./attachments";
import type { ParkingArea } from './parking';
import type { Stairwell } from './stairwells';
import type { StorageUnit } from './storageUnits';
import type { Unit } from './units';

export interface Building {
    key: string;
    summary: BuildingSummary;
    model: BuildingModel;
}

/**
 * Represents an offline lock on a building.
 * When a user downloads a building for offline editing in the iOS app,
 * this lock prevents other users from editing the same building.
 */
export interface OfflineLock {
    lockedBy: string;   // UID of the user holding the lock
    lockedAt: string;   // "YYYY-MM-DD hh:mm:ss" Stockholm timezone
}

export interface BuildingSummary {
    key: string;
    buildingType?: BuildingType;
    name?: string;
    coordinates?: CoordinatesModel;
    createdWhen?: string;
    locationCategories?: LocationCategories;
    currentCoverPhotoKey?: string;
    coverPhoto?: CoverPhotoModel;
    coverPhotos?: { [key: string]: PhotoModel };
    groupTags?: { [key: string]: GroupTag };
    buildingInfo?: BuildingInfo;
    responsibleGroups?: string[];
    offlineLock?: OfflineLock;
}

export interface BuildingModel {
    key: string;
    structure?: BuildingStructure;
    documents?: { [key: string]: DocumentModel };
    currentMapImageKey?: string;
    mapImages?: { [key: string]: PhotoModel };
    todos?: { [key: string]: TodoItem };
    /** Parking areas with nested spots; sibling of structure so offline download covers it. */
    parking?: { [areaKey: string]: ParkingArea };
    /** Storage units (förråd), flat, each bound to a level; sibling of structure for the same reason. */
    storageUnits?: { [unitKey: string]: StorageUnit };
    /** Lägenheter and lokaler (units plan D1); flat like storageUnits. */
    units?: { [unitKey: string]: Unit };
    /** Trappuppgångar the units point at (Unit.stairwellKey); flat like units. */
    stairwells?: { [stairwellKey: string]: Stairwell };
    /** Custom timeline events (`buildings/{key}/timelineItems`); present on the raw node the building context loads. */
    timelineItems?: { [itemKey: string]: CustomTimelineItem };
}

export interface BuildingStructure {
    components?: { [key: string]: BaseComponent };
    rooms?: { [key: string]: RoomModel };
    levels?: { [key: string]: LevelModel };
    /** The building's roof drawing (takplan). Optional; same shape as a level's drawing. */
    roofPlan?: RoofPlan;
}

/**
 * `buildings/{b}/structure/roofPlan`: the roof drawing the takplan view is
 * drawn on. The takplan itself is not stored — it is the set of roof
 * components (see Helpers/roofPlan.ts). Positions on the drawing live in the
 * components' `blueprintLocations/{imageKey}` exactly as on a level drawing.
 */
export interface RoofPlan {
    currentBlueprintKey?: string;
    blueprints?: { [key: string]: PhotoModel };
}

export interface ModularSize {
    width: number;  // in meters
    height: number; // in meters
}

export enum DoorCategory {
    interior_door = "interior_door",
    exterior_door = "exterior_door"
}

export enum ValueStatus {
    notSet = "notSet",
    inaccessible = "inaccessible",
    set = "set"
}

export interface ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;
}

// Document attached to a component (manuals, receipts, certificates)
// Same structure as building documents (FileDocument)
export interface ComponentDocument {
    key: string;
    name: string;
    storageFilename: string;
    fileSize: number;
    uploadedAt: string;
    uploadedBy: string;
    category?: string;
}

export interface ConditionModel {
    value: number;
    comment?: string | null;
}

export interface GroupTag {
    key: string;
    tag: string;
}

export interface canHavePhotos {
    photos?: { [key: string]: PhotoModel };
}

export interface canHaveLabel {
    label?: string;
}

export interface canHaveCondition {
    condition?: number;
}

export interface canHaveComponentType {
    specificType?: ValueWithMetadata<string>;
}

export interface canHaveBrand {
    brand?: ValueWithMetadata<string>;
}

export interface BaseComponent {

    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: ValueWithMetadata<string>;
    condition?: ValueWithMetadata<ConditionModel>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<string[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    thickness?: ValueWithMetadata<number>;
    width?: ValueWithMetadata<number>;
    height?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    linearMeters?: ValueWithMetadata<number>;
    label?: ValueWithMetadata<string>;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    canHaveLock?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    roomKey?: ValueWithMetadata<string>;
    parentSurface1?: ValueWithMetadata<string>;
    parentSurface2?: ValueWithMetadata<string>;
    parentSurface?: ValueWithMetadata<string>;
    parentKey?: ValueWithMetadata<string>;
    blueprintLocations?: { [key: string]: BlueprintLocation };
    //lockMechanism?: ValueWithMetadata<LockMechanismModel>;
    utilityCategory?: UtilityCategory;
    outdoorCategory?: OutdoorCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;
    brand?: canHaveBrand['brand'];  // Utility and fixed furnishing: manufacturer/brand (e.g. Nibe), written by iOS and web (admin)
    mapLocations?: { [key: string]: BlueprintLocation };
    /**
     * Window, door, utility: one pin per photo the component is marked in, keyed by
     * the photo's push key under `components/{parentKey}/photos/{photoKey}`. Any current
     * parent surface qualifies; the side (utsida/insida) is derived from the parent's
     * type, never stored. Written by iOS; helpers in `src/Helpers/photoLocations.ts`.
     */
    photoLocations?: { [photoKey: string]: PhotoLocation };

    // Warranty tracking fields
    warrantyStartDate?: ValueWithMetadata<string>;      // YYYY-MM-DD (date only)
    warrantyDurationYears?: ValueWithMetadata<number>;  // Duration in years

    // Component documents (manuals, receipts, certificates, etc.)
    documents?: { [key: string]: ComponentDocument };

    // Archive and replacement tracking
    archivedAt?: string;          // Stockholm timestamp when archived
    archivedBy?: string;          // UID who archived
    replacedFromKey?: string;     // Key of component this one replaced
    replacedByKey?: string;       // Key of component that replaced this one

    // RoofSafety-specific fields (BaseType.RoofSafety)
    safetyStatus?: ValueWithMetadata<RoofSafetyStatus>;
    anchorPoints?: ValueWithMetadata<{ [key: string]: RoofAnchorPoint }>;
    accessInfo?: ValueWithMetadata<string>;
    accessPositions?: ValueWithMetadata<{ [key: string]: RoofAccessPoint }>;
    parentComponentKey?: string;

    // GPS coordinates for exterior components
    gpsCoordinates?: ValueWithMetadata<CoordinatesModel>;

    // Window-specific fields (BaseType.Window)
    uvValue?: ValueWithMetadata<number>;  // U-value (thermal insulation)

    displayName(building: Building): string;
    totalArea(building: Building): number | null;
}

/**
 * Finds and returns the translation of the first toilet component found in the building model
 * Only applies to toiletReserve and toiletArchipelago building types
 */
export function getToiletComponentTranslation(
    summary: BuildingSummary,
    model: BuildingModel
): string | null {
    // Check if building type is one of the toilet types
    if (
        summary.buildingType !== BuildingType.toiletReserve &&
        summary.buildingType !== BuildingType.toiletArchipelago
    ) {
        return null;
    }

    // Check if model has components
    if (!model.structure?.components) {
        return null;
    }

    // Define the toilet component types we're looking for
    const toiletComponentTypes = [
        "utility_toilet_water",
        "utility_toilet_closed",
        "utility_toilet_solar",
        "utility_toilet_compost",
        "utility_toilet_waste_barrels"
    ];

    // Iterate through all components to find the first toilet type
    for (const component of Object.values(model.structure.components)) {
        const specificTypeValue = component.specificType?.value;
        if (specificTypeValue && toiletComponentTypes.includes(specificTypeValue)) {
            return translate(specificTypeValue);
        }
    }

    return null;
}

export interface WorkEnvironmentRisk {
    description: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    attachments?: { [key: string]: FileAttachment };
}

/**
 * "Golvyta uppmätt på ritning": the room's floor area was measured on a
 * drawing and the floors' shapes set from it (web, RoomFloorAreaModal). The
 * area itself is not stored here; it stays the sum of the floors'
 * `areaShapes`. Current only while no floor's `areaShapes.updatedWhen` is
 * later than `verifiedWhen` (see Helpers/floorAreaVerification.ts). Written
 * and removed by the web only; iOS reads it.
 */
export interface FloorAreaVerification {
    verifiedBy: string;
    verifiedWhen: string;
    comment?: string;
}

export interface IsRoom extends canHaveLabel, canHavePhotos {
    key: string;
    number?: string;
    /** Primary level. For a room spanning several levels, the lowest at save time. */
    levelKey?: string;
    /**
     * Every level the room spans, present only when there are two or more
     * (then it contains `levelKey`). Absent = `{ [levelKey]: true }`. Only
     * drawings, relocation and unit level derivation read it (roomUtils).
     */
    levelKeys?: { [levelKey: string]: true };
    blueprintLocations?: { [key: string]: BlueprintLocation };
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
    electricalInstallation?: ValueWithMetadata<ElectricalInstallation>;
    airTerminalInstallation?: ValueWithMetadata<AirTerminalInstallation>;
    radiatorInstallation?: ValueWithMetadata<RadiatorInstallation>;
    workEnvironmentRisk?: WorkEnvironmentRisk;
    hasPoorReception?: boolean;
    floorAreaVerification?: FloorAreaVerification;
}

export interface FileModel {
    fileType?: string;
    key: string;
    name: string;
    uploadedAt?: string;
    url?: string;
}

export interface BuildingWithCoverUrl extends BuildingModel {
    coverPhotoUrl?: string;
}

export interface ColorModel {
    colorEnum?: ColorEnum;
    code?: string;
}
export interface PhotoModel {
    key: string;
    author: string;
    date: string;
    width: number;
    height: number;
    storagePath: string;
    fileSize?: number;
    /** Drawings only (level, roof and parking blueprints): the single-page PDF the viewers render.
     *  Satellite images and component photos never carry it. Absent on unmigrated drawings. */
    source?: BlueprintSource;
}

/**
 * The vector source of a drawing. Lives at `blueprints/{key}/source`; the object is the raster's
 * sibling `<raster storagePath>_source` (application/pdf, one page). The raster stays the marker
 * coordinate reference (`width`/`height` on the PhotoModel). Shared with iOS and Cloud Functions.
 */
export interface BlueprintSource {
    storagePath: string;
    fileSize: number;
    contentType: 'application/pdf';
    /** pdf = a page of a customer PDF; image = a wrapped raster (image upload, iOS-drawn plan, migration) */
    origin: 'pdf' | 'image';
    /** 1-based page in the original file (origin pdf), provenance only */
    sourcePage?: number;
    /** original file name, display only */
    sourceFileName?: string;
    author: string;
    date: string;
}

export interface PhotoUrlData {
    fullSizeUrl: string;
    mediumSizeUrl: string;
    smallSizeUrl: string;
    thumbnailsGenerated: boolean;
    updatedAt: number;
}

export interface CoverPhotoModel extends Record<string, PhotoUrlData | string | number> {
    author: string;
    date: string;
    height: number;
    key: string;
    storagePath: string;
    width: number;
}

export interface HazardousModel {
    asbestos?: HazardousState;
    pcb?: HazardousState;
    led?: HazardousState;
    arsenic?: HazardousState;
    blueConcrete?: HazardousState;
    radon?: HazardousState;
}

export interface ElectricalInstallation {
    lightSwitchCount?: number;
    lampCount?: number;
    powerOutletCount?: number;
    lightSwitchPhotos?: Record<string, PhotoModel>;
    lampPhotos?: Record<string, PhotoModel>;
    powerOutletPhotos?: Record<string, PhotoModel>;
}

export interface AirTerminalInstallation {
    exhaustCount?: number;
    supplyCount?: number;
    supplyAndExhaustCount?: number;
}

export interface RadiatorInstallation {
    waterRadiatorCount?: number;
    electricRadiatorCount?: number;
}

export interface CoordinatesModel {
    latitude: number;
    longitude: number;
}

export interface BlueprintLocation {
    key: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * A marker in one parent-surface photo. `parentKey` is the surface whose photo it
 * is; `width`/`height` is the photo's pixel size when the pin was placed, the
 * reference frame for `x`/`y`.
 */
export interface PhotoLocation {
    parentKey: string;
    storagePath: string;
    width: number;
    height: number;
    x: number;
    y: number;
}

export interface RoomModel extends IsRoom, canHaveLabel, canHavePhotos { }

export interface LevelModel {
    key: string;
    name?: string;
    number: number;
    ceilingHeight?: number;
    knownArea?: number; // m², matches iOS LevelModel.knownArea
    type?: LevelType;
    currentBlueprintKey?: string;
    blueprints?: { [key: string]: PhotoModel };
}

export interface RiskConstruction {
    risks?: RiskConstructionType[];
    customRisk?: string;
}

export interface LocationCategories {
    category1?: LocationCategory;
    category2?: LocationCategory;
    category3?: LocationCategory;
    category4?: LocationCategory;
    category5?: LocationCategory;
}

export interface LocationCategory {
    name: string;
    key: String;
}

export interface BuildingInfo {
    keyInstructions?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    other?: string;
    hasPoorReception?: boolean;
    seasonInfo?: SeasonInfo;
    heritageProtectionLevel?: HeritageProtectionLevel[];
    yearBuilt?: number;
    defaultWarrantyStartDate?: string;  // YYYY-MM-DD, for pre-filling warranty start on new components
}

export interface SeasonInfo {
    comment?: string;
}

export interface ContactModel {
    key: string;
    firstName: string;
    lastName: string;
    companyKey?: string;
    role?: string;
    phoneNumber?: string;
    email?: string;
    info?: string;
    createdBy: string;
    createdWhen: string;
}

export interface CompanyModel {
    key: string;
    name: string;
    createdBy: string;
    createdWhen: string;
}

export type ContactSortField = 'firstName' | 'lastName' | 'company' | 'role';

export interface RoofAccessPoint {
    photos?: { [key: string]: PhotoModel };
    position: BlueprintLocation;
    comment?: string;
}

export interface RoofAnchorPoint {
    photos?: { [key: string]: PhotoModel };
    position?: BlueprintLocation;
    safetyStatus: AnchorPointSafetyStatus;
    comment?: string;
}

export enum AnchorPointSafetyStatus {
    SAFE = "SAFE",
    NOT_SAFE = "NOT_SAFE",
    UNDETERMINED = "UNDETERMINED"
}
